import type { CalendarEntry } from "@kuroo/contracts";

export type CalendarView = "month" | "week" | "day" | "list" | "timeline";
export type CalendarRange = { fromDate: string; toDate: string; days: string[]; from: string; to: string; label: string };

export function dateInTimezone(timezone = "UTC", value = new Date()): string {
  try {
    const parts = new Intl.DateTimeFormat("en-CA", { timeZone: timezone, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(value);
    const result = Object.fromEntries(parts.filter((part) => part.type !== "literal").map((part) => [part.type, part.value]));
    return `${result.year}-${result.month}-${result.day}`;
  } catch { return value.toISOString().slice(0, 10); }
}

/** Convert a calendar date in the business timezone into an exact UTC boundary. */
export function businessDateBoundary(date: string, timezone: string, end = false): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return new Date().toISOString();
  const [year, month, day] = date.split("-").map(Number);
  const baseNaive = Date.UTC(year, month - 1, day, end ? 23 : 0, end ? 59 : 0, end ? 59 : 0);
  const naive = baseNaive + (end ? 999 : 0);
  try {
    const parts = new Intl.DateTimeFormat("en-US", { timeZone: timezone, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" }).formatToParts(new Date(naive));
    const result = Object.fromEntries(parts.filter((part) => part.type !== "literal").map((part) => [part.type, part.value]));
    const rendered = Date.UTC(Number(result.year), Number(result.month) - 1, Number(result.day), Number(result.hour), Number(result.minute), Number(result.second));
    return new Date(naive - (rendered - baseNaive)).toISOString();
  } catch {
    return new Date(naive).toISOString();
  }
}

function calendarDate(value: string): Date {
  return new Date(`${value}T00:00:00Z`);
}

function calendarDateValue(value: Date): string {
  return value.toISOString().slice(0, 10);
}

export function addCalendarDays(value: string, days: number): string {
  const result = calendarDate(value);
  result.setUTCDate(result.getUTCDate() + days);
  return calendarDateValue(result);
}

export function addCalendarMonths(value: string, months: number): string {
  const result = calendarDate(value);
  result.setUTCDate(1);
  result.setUTCMonth(result.getUTCMonth() + months);
  return calendarDateValue(result);
}

export function calendarWeekStart(value: string): string {
  const result = calendarDate(value);
  const day = result.getUTCDay();
  result.setUTCDate(result.getUTCDate() - (day === 0 ? 6 : day - 1));
  return calendarDateValue(result);
}

export function calendarCivilLabel(value: string, options: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat("es-AR", { ...options, timeZone: "UTC" }).format(calendarDate(value));
}

export function makeCalendarRange(view: CalendarView, anchor: string, timezone: string): CalendarRange {
  const safeAnchor = /^\d{4}-\d{2}-\d{2}$/.test(anchor) ? anchor : dateInTimezone(timezone);
  let fromDate = safeAnchor;
  let toDate = addCalendarDays(safeAnchor, 1);
  if (view === "month") {
    const first = `${safeAnchor.slice(0, 7)}-01`;
    fromDate = calendarWeekStart(first);
    const last = calendarDateValue(new Date(Date.UTC(calendarDate(first).getUTCFullYear(), calendarDate(first).getUTCMonth() + 1, 0)));
    toDate = addCalendarDays(calendarWeekStart(last), 7);
  } else if (view === "week" || view === "list" || view === "timeline") {
    fromDate = calendarWeekStart(safeAnchor);
    toDate = addCalendarDays(fromDate, 7);
  }
  const days: string[] = [];
  for (let cursor = fromDate; cursor < toDate; cursor = addCalendarDays(cursor, 1)) days.push(cursor);
  const label = view === "month"
    ? calendarCivilLabel(safeAnchor, { month: "long", year: "numeric" })
    : view === "day"
      ? calendarCivilLabel(safeAnchor, { weekday: "long", day: "numeric", month: "long", year: "numeric" })
      : `${calendarCivilLabel(fromDate, { day: "numeric", month: "short" })} – ${calendarCivilLabel(addCalendarDays(toDate, -1), { day: "numeric", month: "short", year: "numeric" })}`;
  return { fromDate, toDate, days, from: businessDateBoundary(fromDate, timezone), to: businessDateBoundary(toDate, timezone), label };
}

export function calendarShift(value: string, view: CalendarView, amount: number): string {
  if (view === "month") return addCalendarMonths(value, amount);
  if (view === "week" || view === "list" || view === "timeline") return addCalendarDays(value, amount * 7);
  return addCalendarDays(value, amount);
}

export function calendarTime(value: string, timezone: string): string {
  return new Intl.DateTimeFormat("es-AR", { timeZone: timezone, hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(new Date(value));
}

export function calendarEntryTitle(entry: CalendarEntry): string {
  if (entry.kind === "event") return entry.eventName || "Evento";
  if (entry.kind === "hold") return entry.serviceName || "En espera";
  return entry.serviceName || entry.customerName || "Reserva";
}

export function calendarHoldRemaining(entry: CalendarEntry, now = Date.now()): string | undefined {
  if (entry.kind !== "hold" || !entry.expiresAt) return undefined;
  const expires = Date.parse(entry.expiresAt);
  if (!Number.isFinite(expires)) return undefined;
  const remaining = Math.max(0, expires - now);
  const minutes = Math.floor(remaining / 60_000);
  const seconds = Math.floor((remaining % 60_000) / 1_000);
  return remaining > 0 ? `${minutes}m ${String(seconds).padStart(2, "0")}s restantes` : "Vencido; actualizando…";
}

export function calendarEntryMeta(entry: CalendarEntry, now = Date.now()): string {
  if (entry.kind === "event") return [entry.providerNames?.length ? entry.providerNames.join(", ") : "Sin empleado asignado", entry.locationName, entry.capacity !== undefined ? `${entry.capacity} lugares` : undefined].filter(Boolean).join(" · ");
  if (entry.kind === "hold") return [entry.customerName, entry.participantCount ? `${entry.participantCount} persona${entry.participantCount === 1 ? "" : "s"}` : undefined, entry.providerName, entry.locationName, calendarHoldRemaining(entry, now)].filter(Boolean).join(" · ") || "En espera";
  return [entry.customerName, entry.participantCount ? `${entry.participantCount} persona${entry.participantCount === 1 ? "" : "s"}` : undefined, entry.providerName, entry.locationName].filter(Boolean).join(" · ") || "Sin datos adicionales";
}

export function calendarStatusLabel(status: string): string {
  const labels: Record<string, string> = { confirmed: "Confirmada", held: "En espera", pending: "Pendiente", cancelled: "Cancelada", closed: "Cerrado", open: "Abierto", archived: "Archivado" };
  return labels[status] ?? status;
}

export function calendarEntryFaded(entry: CalendarEntry): boolean {
  return ["cancelled", "closed", "archived"].includes(entry.status) || entry.eventStatus === "cancelled";
}

export function calendarEntriesForDay(entries: CalendarEntry[], day: string, timezone: string): CalendarEntry[] {
  const start = Date.parse(businessDateBoundary(day, timezone));
  const end = Date.parse(businessDateBoundary(addCalendarDays(day, 1), timezone));
  return entries.filter((entry) => Date.parse(entry.startAt) < end && Date.parse(entry.endAt) > start);
}

export function calendarEntryPosition(entry: CalendarEntry, day: string, timezone: string): { top: number; height: number } {
  const dayStart = Date.parse(businessDateBoundary(day, timezone));
  const dayEnd = Date.parse(businessDateBoundary(addCalendarDays(day, 1), timezone));
  const start = Math.max(dayStart, Math.min(dayEnd, Date.parse(entry.startAt)));
  const end = Math.max(start + 15 * 60_000, Math.min(dayEnd, Date.parse(entry.endAt)));
  const top = Math.max(0, (start - dayStart) / 60_000) * 1.4;
  const height = Math.max(34, (end - start) / 60_000 * 1.4);
  return { top, height };
}
